import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Button, Card, EmptyState, LoadingList, SectionHeader, cx, useIsPhone,
} from '@timely/ui';
import {
    AGEING_BANDS, DEFAULT_FILTERS, KIND_CAPTION, REPORT_PRESETS, filtersFromParams,
    holds, isReportPreset, periodFromParams, periodLength, csvFileName,
    type ReportPeriod, type ReportPreset,
} from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { useFormatters } from '../book/useFormatters';
import {
    ExportError, downloadCsv, exportCustomers, useReports,
    type ActivityTotals, type AgeingRow, type MemberReport, type Receivables,
} from '../reports/useReports';

/**
 * Reports: what the company is owed, and what its people did about it.
 *
 * Three rules shape it, and they are the same three that shaped Today.
 *
 * **Every figure is a door.** A number nobody can open is a number nobody can
 * check, and a report full of them is decoration. Each one that has a matching
 * customer set links to the Book filtered to exactly the accounts it counted —
 * the ageing rows carry the filter value the database counted them by, so the
 * two cannot drift.
 *
 * **Current state and period activity are kept apart**, in separate sections
 * with captions saying which is which. They are different kinds of fact, and a
 * screen that mixes them is a screen where somebody changes the month and
 * wonders why the outstanding figure did not move.
 *
 * **An aggregate is as much of a disclosure as a row.** Every figure comes from
 * a `security invoker` function, so a collector's report is of their own
 * accounts. The team section needs `reports.view.all`; the export needs
 * `reports.export`, which is a right of its own — a collector may read their
 * own report and may not take it out of the building.
 */
export function Reports() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const { count, date, today } = useFormatters();
    const isPhone = useIsPhone();

    const canSeeAll = holds(context, 'reports.view.all');
    const canExport = holds(context, 'reports.export');

    const [params, setParams] = useSearchParams();
    const period = periodFromParams(params, today);

    const { receivables, ageing, activity, team, teamUnavailable, loading, error, reload } =
        useReports(context, period, { withTeam: canSeeAll });

    const [exporting, setExporting] = useState(false);
    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

    const book = (query: string) => `/w/${workspace.slug}/book${query ? `?${query}` : ''}`;

    const setPeriod = (changes: { period?: ReportPreset; from?: string; to?: string }) => {
        const next = new URLSearchParams(params);
        for (const [key, value] of Object.entries(changes)) {
            if (!value) next.delete(key); else next.set(key, value);
        }
        setParams(next, { replace: true });
    };

    const runExport = async () => {
        if (exporting) return;
        setExporting(true);
        setNotice(null);
        try {
            // The filters the Book would use for "everything with dues" — the
            // same question, answered again by the database under this
            // person's own authorisation.
            const result = await exportCustomers(
                workspace.id, { ...DEFAULT_FILTERS }, workspace.currency,
            );
            if (result.rowCount === 0) {
                setNotice({ tone: 'error', text: 'There is nothing to export.' });
                return;
            }
            downloadCsv(
                csvFileName('customers', workspace.name, date.todayValue()),
                result.csv,
            );
            setNotice({
                tone: 'ok',
                text: result.capped
                    ? `Exported the largest ${count(result.rowCount)} accounts. Narrow the book and export again for the rest.`
                    : `Exported ${count(result.rowCount)} accounts.`,
            });
        } catch (failed) {
            setNotice({
                tone: 'error',
                text: failed instanceof ExportError ? failed.message : 'That export did not work.',
            });
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <Page>
                <SectionHeader title="Reports" subtitle="Where the money is, and what is being done about it." />
                <Card className="mt-5 overflow-hidden"><LoadingList label="Building your report" rows={5} /></Card>
            </Page>
        );
    }

    if (error) {
        return (
            <Page>
                <SectionHeader title="Reports" subtitle="Where the money is, and what is being done about it." />
                <div className="mt-4"><FormNotice tone="error">{error}</FormNotice></div>
                <div className="mt-4">
                    <Button variant="secondary" onClick={() => { void reload(); }}>Try again</Button>
                </div>
            </Page>
        );
    }

    // A company with nothing in the book has not started, and six zeroes would
    // be six ways of saying so badly.
    if (!receivables || (receivables.withDuesCount === 0 && receivables.settledCount === 0)) {
        return (
            <Page>
                <SectionHeader title="Reports" subtitle="Where the money is, and what is being done about it." />
                <Card className="mt-5">
                    <EmptyState
                        title="No receivables yet"
                        hint={holds(context, 'datasource.manage')
                            ? 'Connect your receivables sheet and sync it, and every figure here fills in.'
                            : 'Once somebody connects your receivables sheet, this fills in.'}
                        action={holds(context, 'datasource.manage')
                            ? (
                                <Link to={`/w/${workspace.slug}/data`} className="text-accent font-semibold hover:underline">
                                    Connect a sheet
                                </Link>
                            )
                            : undefined}
                    />
                </Card>
            </Page>
        );
    }

    return (
        <Page>
            <SectionHeader
                title="Reports"
                subtitle="Where the money is, and what is being done about it."
                actions={canExport ? (
                    <Button variant="secondary" disabled={exporting} onClick={() => { void runExport(); }}>
                        {exporting ? 'Building…' : 'Export accounts'}
                    </Button>
                ) : undefined}
            />

            {notice && <div className="mt-4"><FormNotice tone={notice.tone}>{notice.text}</FormNotice></div>}

            {/* ------------------------------ current ------------------------------ */}

            <div className="mt-7">
                <SectionHeader title="Where the money is" />
            </div>
            <p className="text-[12px] text-label-3 mt-1">{KIND_CAPTION.current}</p>

            <Position receivables={receivables} book={book} />

            <div className="mt-9"><SectionHeader title="How old the money is" /></div>
            <Ageing rows={ageing} receivable={receivables.receivable} book={book} isPhone={isPhone} />

            {/* ------------------------------ period ------------------------------- */}

            <div className="mt-9">
                <SectionHeader
                    title="What was done"
                    actions={<PeriodPicker period={period} onChange={setPeriod} />}
                />
            </div>
            <p className="text-[12px] text-label-3 mt-1">
                {KIND_CAPTION.period} {date.day(`${period.from}T12:00:00`)} to{' '}
                {date.day(`${period.to}T12:00:00`)} · {count(periodLength(period))}{' '}
                {periodLength(period) === 1 ? 'day' : 'days'}.
            </p>

            <Activity activity={activity} slug={workspace.slug} />

            {canSeeAll && (
                <Team
                    team={team}
                    unavailable={teamUnavailable}
                    book={book}
                    isPhone={isPhone}
                />
            )}
        </Page>
    );
}

/* -------------------------------- the period ------------------------------ */

function PeriodPicker({
    period, onChange,
}: {
    period: ReportPeriod;
    onChange: (changes: { period?: ReportPreset; from?: string; to?: string }) => void;
}) {
    return (
        <div className="flex items-end gap-2 flex-wrap">
            <div>
                <label htmlFor="report-period" className="block text-[12px] font-semibold text-label mb-1">
                    Period
                </label>
                <select
                    id="report-period"
                    value={period.preset}
                    onChange={e => onChange({ period: e.target.value as ReportPreset })}
                    className="h-10 px-3 rounded-[10px] bg-card border border-separator-strong text-[13.5px] text-label outline-none focus:border-accent"
                >
                    {REPORT_PRESETS.map(preset => (
                        <option key={preset.preset} value={preset.preset} title={preset.hint}>
                            {preset.label}
                        </option>
                    ))}
                </select>
            </div>
            {period.preset === 'custom' && (
                <>
                    <div>
                        <label htmlFor="report-from" className="block text-[12px] font-semibold text-label mb-1">
                            From
                        </label>
                        <input
                            id="report-from" type="date" value={period.from}
                            onChange={e => onChange({ period: 'custom', from: e.target.value })}
                            className="h-10 px-3 rounded-[10px] bg-card border border-separator-strong text-[13.5px] text-label outline-none focus:border-accent"
                        />
                    </div>
                    <div>
                        <label htmlFor="report-to" className="block text-[12px] font-semibold text-label mb-1">
                            To
                        </label>
                        <input
                            id="report-to" type="date" value={period.to}
                            onChange={e => onChange({ period: 'custom', to: e.target.value })}
                            className="h-10 px-3 rounded-[10px] bg-card border border-separator-strong text-[13.5px] text-label outline-none focus:border-accent"
                        />
                    </div>
                </>
            )}
        </div>
    );
}

/* ------------------------------ the position ------------------------------ */

/**
 * One figure, and the accounts behind it.
 *
 * A figure with no `to` is one that has no matching customer list — the credit
 * total, for instance, where the Book has no "in credit" filter. It renders as
 * a plain figure rather than as a link that goes somewhere approximate.
 */
function Figure({
    label, value, sub, to, tone = 'neutral',
}: {
    label: string;
    value: string;
    sub?: string;
    to?: string;
    tone?: 'neutral' | 'dang' | 'warn' | 'pos';
}) {
    const toneClass = {
        neutral: 'text-label', dang: 'text-dang', warn: 'text-warn', pos: 'text-pos',
    }[tone];

    const body = (
        <>
            <div className={cx('text-[19px] font-bold num tabular-nums leading-none', toneClass)}>
                {value}
            </div>
            <div className="text-[12px] text-label-2 mt-1.5 font-semibold">{label}</div>
            {sub && <div className="text-[11.5px] text-label-3 mt-0.5">{sub}</div>}
        </>
    );

    if (!to) return <Card className="px-4 py-3.5 min-h-[92px]">{body}</Card>;

    return (
        <Link
            to={to}
            aria-label={`${label}: ${value}`}
            className="block rounded-[14px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
            <Card className="px-4 py-3.5 min-h-[92px] hover:border-separator-strong transition-colors">
                {body}
            </Card>
        </Link>
    );
}

function Position({
    receivables, book,
}: {
    receivables: Receivables;
    book: (query: string) => string;
}) {
    const { money, count } = useFormatters();

    return (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Figure
                label="Outstanding"
                value={money.exact(receivables.outstanding)}
                sub={`${count(receivables.withDuesCount)} accounts with dues`}
                to={book('')}
            />
            <Figure
                label="Overdue follow-ups"
                value={count(receivables.overdueCount)}
                sub={money.compact(receivables.overdueAmount)}
                tone={receivables.overdueCount > 0 ? 'dang' : 'neutral'}
                to={book('followUp=overdue')}
            />
            <Figure
                label="Promised"
                value={money.exact(receivables.promisedAmount)}
                sub={`${count(receivables.promisedCount)} accounts — expected, not received`}
            />
            <Figure
                label="Held for customers"
                value={money.exact(receivables.creditAmount)}
                sub={`${count(receivables.creditCount)} in credit`}
            />
            <Figure
                label="No follow-up set"
                value={count(receivables.noFollowUpCount)}
                sub="Money owed and nothing planned"
                tone={receivables.noFollowUpCount > 0 ? 'warn' : 'neutral'}
                to={book('followUp=no_follow_up')}
            />
            <Figure
                label="Due today"
                value={count(receivables.dueTodayCount)}
                to={book('followUp=today')}
            />
            <Figure
                label="Marked urgent"
                value={count(receivables.urgentCount)}
                tone={receivables.urgentCount > 0 ? 'dang' : 'neutral'}
                to={book('urgentOnly=true')}
            />
            <Figure
                label="Written off"
                value={count(receivables.badDebtCount)}
                sub={money.compact(receivables.badDebtAmount)}
                to={book('rank=Bad')}
            />
        </div>
    );
}

/* -------------------------------- the ageing ------------------------------ */

/**
 * How old the money is.
 *
 * A table, not a chart. The figures are the point, they need to be read exactly,
 * and a bar somebody has to hover to read a number from is a worse version of
 * the number. The share bar beside each row is decoration on top of a stated
 * percentage, never instead of it — and it is `aria-hidden`, because it says
 * nothing the row does not.
 *
 * Every row opens the accounts it counted: `filterValue` comes from the same
 * query that produced the count.
 */
function Ageing({
    rows, receivable, book, isPhone,
}: {
    rows: AgeingRow[];
    receivable: number;
    book: (query: string) => string;
    isPhone: boolean;
}) {
    const { money, count } = useFormatters();
    const total = rows.reduce((sum, row) => sum + row.amount, 0);

    if (total === 0) {
        return (
            <Card className="mt-4">
                <EmptyState
                    title="Nothing is ageing"
                    hint="No account is carrying a debit balance in any age band."
                />
            </Card>
        );
    }

    const share = (amount: number) => (total > 0 ? Math.round((amount / total) * 100) : 0);
    const label = (bucket: string) =>
        AGEING_BANDS.find(b => b.bucket === bucket)?.label ?? bucket;

    if (isPhone) {
        return (
            <Card className="mt-4 overflow-hidden">
                <ul className="divide-y divide-separator">
                    {rows.map(row => (
                        <li key={row.bucket}>
                            <Link
                                to={book(`ageing=${row.filterValue}&settlement=all`)}
                                className="block px-4 py-3 min-h-[56px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                aria-label={`${label(row.bucket)}: ${money.exact(row.amount)} across ${count(row.accountCount)} accounts`}
                            >
                                <div className="flex items-baseline justify-between gap-3">
                                    <span className="text-[13.5px] font-semibold text-label">
                                        {label(row.bucket)}
                                    </span>
                                    <span className="text-[15px] font-bold text-label num tabular-nums">
                                        {money.compact(row.amount)}
                                    </span>
                                </div>
                                <div className="text-[12px] text-label-3 mt-0.5">
                                    {count(row.accountCount)}{' '}
                                    {row.accountCount === 1 ? 'account' : 'accounts'} · {share(row.amount)}%
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>
            </Card>
        );
    }

    return (
        <Card className="mt-4 overflow-hidden">
            <table className="w-full text-[13px]">
                <caption className="sr-only">
                    Outstanding debit by age band, with the share of the total each band carries.
                </caption>
                <thead>
                    <tr className="text-left text-label-3 text-[12px]">
                        <th scope="col" className="font-semibold px-5 py-2.5">Age</th>
                        <th scope="col" className="font-semibold px-3 py-2.5 text-right">Amount</th>
                        <th scope="col" className="font-semibold px-3 py-2.5 text-right">Accounts</th>
                        <th scope="col" className="font-semibold px-3 py-2.5 text-right">Share</th>
                        <th scope="col" className="font-semibold px-5 py-2.5"><span className="sr-only">Open</span></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-separator">
                    {rows.map(row => (
                        <tr key={row.bucket}>
                            <th scope="row" className="px-5 py-2.5 font-semibold text-label text-left">
                                {label(row.bucket)}
                            </th>
                            <td className="px-3 py-2.5 text-right num tabular-nums text-label">
                                {money.exact(row.amount)}
                            </td>
                            <td className="px-3 py-2.5 text-right num tabular-nums text-label-2">
                                {count(row.accountCount)}
                            </td>
                            <td className="px-3 py-2.5 text-right num tabular-nums text-label-2">
                                {share(row.amount)}%
                                <span
                                    aria-hidden="true"
                                    className="inline-block ml-2 h-1.5 rounded-full bg-accent align-middle"
                                    style={{ width: `${Math.max(2, share(row.amount) * 0.6)}px` }}
                                />
                            </td>
                            <td className="px-5 py-2.5 text-right">
                                <Link
                                    to={book(`ageing=${row.filterValue}&settlement=all`)}
                                    className="text-[12.5px] text-accent font-semibold hover:underline"
                                    aria-label={`Open the ${count(row.accountCount)} accounts with money ${label(row.bucket).toLowerCase()} old`}
                                >
                                    Open
                                </Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className="border-t border-separator-strong">
                        <th scope="row" className="px-5 py-2.5 text-left text-label-2">Total</th>
                        <td className="px-3 py-2.5 text-right num tabular-nums font-bold text-label">
                            {money.exact(total)}
                        </td>
                        <td colSpan={3} className="px-3 py-2.5 text-right text-[12px] text-label-3">
                            {total !== receivable && 'Bands may total less than the receivable when a sheet supplies its own roll-ups.'}
                        </td>
                    </tr>
                </tfoot>
            </table>
        </Card>
    );
}

/* ------------------------------- the activity ----------------------------- */

function Activity({ activity, slug }: { activity: ActivityTotals | null; slug: string }) {
    const { money, count } = useFormatters();

    if (!activity) {
        return (
            <Card className="mt-4 px-5 py-4">
                <p className="text-[13px] text-label-3">We could not read the activity for this period.</p>
            </Card>
        );
    }

    if (activity.followUps === 0 && activity.chequesCleared === 0 && activity.accountsSettled === 0) {
        return (
            <Card className="mt-4">
                <EmptyState
                    title="Nothing was recorded in this period"
                    hint="No follow-ups, no cheques cleared and no accounts settled between these dates. Try a longer period."
                />
            </Card>
        );
    }

    return (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Figure label="Follow-ups recorded" value={count(activity.followUps)} />
            <Figure
                label="Accounts contacted"
                value={count(activity.accountsTouched)}
                sub="Distinct customers, not calls"
            />
            <Figure
                label="Promises taken"
                value={count(activity.promises)}
                sub={money.compact(activity.promisedAmount)}
            />
            <Figure
                label="Cheques cleared"
                value={count(activity.chequesCleared)}
                sub={money.compact(activity.chequesClearedAmount)}
                tone={activity.chequesCleared > 0 ? 'pos' : 'neutral'}
                to={`/w/${slug}/cheques?state=cleared`}
            />
            <Figure
                label="Accounts settled"
                value={count(activity.accountsSettled)}
                sub="When a sync first saw the balance clear"
            />
            <Figure label="Calls that reached somebody" value={count(activity.callsAnswered)} />
            <Figure label="No answer or refused" value={count(activity.callsUnanswered)} />
            <Figure
                label="Disputes raised"
                value={count(activity.disputes)}
                tone={activity.disputes > 0 ? 'warn' : 'neutral'}
            />
        </div>
    );
}

/* --------------------------------- the team ------------------------------- */

function Team({
    team, unavailable, book, isPhone,
}: {
    team: MemberReport[];
    unavailable: boolean;
    book: (query: string) => string;
    isPhone: boolean;
}) {
    const { money, count } = useFormatters();

    if (unavailable) {
        return (
            <>
                <div className="mt-9"><SectionHeader title="By person" /></div>
                <Card className="mt-4 px-5 py-4">
                    <p className="text-[13px] text-label-3">We could not read the team figures just now.</p>
                </Card>
            </>
        );
    }

    if (team.length === 0) return null;

    return (
        <>
            <div className="mt-9">
                <SectionHeader
                    title="By person"
                    subtitle="What each person is carrying, and what they did in the period."
                />
            </div>

            {isPhone ? (
                <Card className="mt-4 overflow-hidden">
                    <ul className="divide-y divide-separator">
                        {team.map(member => (
                            <li key={member.memberId} className="px-4 py-3.5">
                                <div className="flex items-baseline justify-between gap-3">
                                    <Link
                                        to={book(`owner=${member.memberId}`)}
                                        className="text-[14px] font-semibold text-label hover:text-accent hover:underline"
                                    >
                                        {member.displayName}
                                    </Link>
                                    <span className="text-[14px] font-bold text-label num tabular-nums">
                                        {money.compact(member.assignedAmount)}
                                    </span>
                                </div>
                                <div className="text-[12px] text-label-3 mt-1">
                                    {count(member.assignedCount)} accounts ·{' '}
                                    <span className={member.overdueCount > 0 ? 'text-dang font-semibold' : ''}>
                                        {count(member.overdueCount)} overdue
                                    </span>{' '}
                                    · {count(member.noFollowUpCount)} unplanned
                                </div>
                                <div className="text-[12px] text-label-3 mt-0.5">
                                    In the period: {count(member.followUps)} follow-ups,{' '}
                                    {count(member.promises)} promises
                                </div>
                            </li>
                        ))}
                    </ul>
                </Card>
            ) : (
                <Card className="mt-4 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-[13px]">
                            <caption className="sr-only">
                                Each person’s assigned book now, and what they recorded during the selected period.
                            </caption>
                            <thead>
                                <tr className="text-left text-label-3 text-[12px]">
                                    <th scope="col" className="font-semibold px-5 py-2.5">Person</th>
                                    <th scope="col" className="font-semibold px-3 py-2.5 text-right">Accounts</th>
                                    <th scope="col" className="font-semibold px-3 py-2.5 text-right">Assigned</th>
                                    <th scope="col" className="font-semibold px-3 py-2.5 text-right">Overdue</th>
                                    <th scope="col" className="font-semibold px-3 py-2.5 text-right">Unplanned</th>
                                    <th scope="col" className="font-semibold px-3 py-2.5 text-right">Follow-ups</th>
                                    <th scope="col" className="font-semibold px-5 py-2.5 text-right">Promises</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-separator">
                                {team.map(member => (
                                    <tr key={member.memberId}>
                                        <th scope="row" className="px-5 py-2.5 text-left font-semibold">
                                            <Link
                                                to={book(`owner=${member.memberId}`)}
                                                className="text-label hover:text-accent hover:underline"
                                            >
                                                {member.displayName}
                                            </Link>
                                        </th>
                                        <td className="px-3 py-2.5 text-right num tabular-nums text-label-2">
                                            {count(member.assignedCount)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right num tabular-nums text-label">
                                            {money.compact(member.assignedAmount)}
                                        </td>
                                        <td className={cx('px-3 py-2.5 text-right num tabular-nums',
                                            member.overdueCount > 0 ? 'text-dang font-semibold' : 'text-label-3')}>
                                            {count(member.overdueCount)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right num tabular-nums text-label-2">
                                            {count(member.noFollowUpCount)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right num tabular-nums text-label-2">
                                            {count(member.followUps)}
                                        </td>
                                        <td className="px-5 py-2.5 text-right num tabular-nums text-label-2">
                                            {count(member.promises)}
                                            {member.promisedAmount > 0 && (
                                                <span className="text-label-3"> · {money.compact(member.promisedAmount)}</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
        </>
    );
}

export { isReportPreset, filtersFromParams };
